const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

export const money = (value) =>
  Number(value || 0).toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  });

export const fmtDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "medium",
  }).format(d);
};

export const fmtDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(d);
};

export const getClaimantLabel = (claim) => {
  const prenom = claim?.Claimant?.prenom || "";
  const nom = claim?.Claimant?.nom || "";
  const email = claim?.Claimant?.email || "";
  const fullName = `${prenom} ${nom}`.trim();
  return fullName || email || `Utilisateur #${claim?.user_id || "?"}`;
};

export const getClaimantInitials = (claim) => {
  const label = getClaimantLabel(claim);
  return label
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");
};

export const getEventLabel = (claim, eventDetailsMap = {}) => {
  const ev = eventDetailsMap?.[claim?.event_id];
  return (
    ev?.titre ||
    claim?.ClaimEvent?.titre ||
    claim?.ClaimEvent?.nom_event ||
    claim?.ClaimEvent?.nom ||
    claim?.ClaimEvent?.title ||
    "Événement"
  );
};

export const getEventDatePlace = (claim, eventDetailsMap = {}) => {
  const ev = eventDetailsMap?.[claim?.event_id];
  if (!ev) return "";

  const parts = [
    ev.start ? fmtDate(ev.start) : null,
    ev.lieu_event || null,
  ].filter(Boolean);

  return parts.join(" · ");
};

export const getEventMeta = (claim, eventDetailsMap = {}) => {
  const ev = eventDetailsMap?.[claim?.event_id];
  if (!ev) return "";

  const parts = [
    ev.type_event,
    ev.level_event,
    ev.cate_event,
    ev.organisateur,
  ].filter(Boolean);

  return parts.join(" · ");
};

export const getBudgetCodeLabel = (claim) => {
  if (!claim?.BudgetCode) return "—";
  return `${claim.BudgetCode.code} · ${claim.BudgetCode.libelle}`;
};

export const statusLabel = (status) => {
  switch (status) {
    case "draft":
      return "Brouillon";
    case "submitted":
      return "Soumise";
    case "approved":
      return "Approuvée";
    case "rejected":
      return "Rejetée";
    case "paid":
      return "Payée";
    default:
      return status || "—";
  }
};

export const statusClasses = (status) => {
  switch (status) {
    case "draft":
      return "bg-slate-100 text-slate-700 ring-slate-200";
    case "submitted":
      return "bg-amber-100 text-amber-800 ring-amber-200";
    case "approved":
      return "bg-emerald-100 text-emerald-800 ring-emerald-200";
    case "rejected":
      return "bg-rose-100 text-rose-800 ring-rose-200";
    case "paid":
      return "bg-sky-100 text-sky-800 ring-sky-200";
    default:
      return "bg-slate-100 text-slate-700 ring-slate-200";
  }
};

export const toAbsoluteFileUrl = (url) => {
  if (!url) return "#";
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_BASE}${url.startsWith("/") ? "" : "/"}${url}`;
};

export const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const cleanFilters = (filters) =>
  Object.fromEntries(
    Object.entries(filters).filter(
      ([, value]) => value !== "" && value !== null && value !== undefined,
    ),
  );

const getSortValue = (claim, sortBy) => {
  switch (sortBy) {
    case "id":
      return Number(claim?.id || 0);
    case "claimant":
      return getClaimantLabel(claim).toLowerCase();
    case "event":
      return getEventLabel(claim).toLowerCase();
    case "budget":
      return getBudgetCodeLabel(claim).toLowerCase();
    case "status":
      return String(claim?.statut || "").toLowerCase();
    case "total_frais":
      return Number(claim?.total_frais || 0);
    case "total_general":
      return Number(claim?.total_general || 0);
    case "date_soumission":
      return claim?.date_soumission
        ? new Date(claim.date_soumission).getTime()
        : 0;
    case "created_at":
    default:
      return claim?.created_at ? new Date(claim.created_at).getTime() : 0;
  }
};

export const sortClaims = (claims, sortBy = "created_at", sortDir = "desc") => {
  const rows = [...(claims || [])];
  rows.sort((a, b) => {
    const av = getSortValue(a, sortBy);
    const bv = getSortValue(b, sortBy);

    if (typeof av === "string" && typeof bv === "string") {
      return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    }

    return sortDir === "asc" ? av - bv : bv - av;
  });
  return rows;
};